import { Box, Typography } from '@material-ui/core';
import React, { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { compact, flatten, isEmpty, isNil } from 'lodash';
import { Divider, TypographyWrapper } from '..';
import { useBrandCountryConfig } from '../../hooks/useCountryConfig';
import { defaultTreetStyles } from '../../shopConfig/config';
import {
  BundleType,
  CoreRefund,
  LineItemCode,
  Participant,
} from '../../types/apollo/generated/types.generated';
import { CheckoutLineItem } from '../../types/models/lineItem';
import {
  calculateLineItemTotal,
  getDiscountTotal,
  getListingsTotal,
  getReturnInsuranceTotal,
  getShippingTotal,
  getTaxTotal,
  SIGNATURE_CONFIRMATION_SHIPPING_LINE_ITEM_NAME,
} from '../../util/lineItems';
import { formatDollarAmount } from '../../util/currency';
import { useShopConfig } from '../../hooks/shopConfig';
import { TypographyWeight } from '../TypographyWrapper/TypographyWrapper';
import BundlePanelInfoTooltip from '../BundlePanel/BundlePanelInfoTooltip';
import { useDefaultShopStyles } from '../../hooks/useDefaultShopStyles';

interface LineItemWrapperProps {
  children: ReactNode;
}

const LineItemWrapper: FC<LineItemWrapperProps> = (props) => (
  <Box display="flex" flexDirection="row" justifyContent="space-between" p={2}>
    {props.children}
  </Box>
);
interface ItemsAndShippingLineItemProps {
  hasShippingLineItems: boolean;
  shippingTotal: number;
  itemsAndShippingTotal: number;
}

const ItemsAndShippingLineItem: FC<ItemsAndShippingLineItemProps> = (props) => {
  const { hasShippingLineItems, shippingTotal, itemsAndShippingTotal } = props;

  const intl = useIntl();
  const { currencyConfig } = useBrandCountryConfig();

  return (
    <>
      <LineItemWrapper>
        <TypographyWrapper
          variant="body2"
          weight={TypographyWeight.Bold}
          typographyOverrides={{ display: 'inline' }}
        >
          {shippingTotal > 0 ? 'Items + Shipping' : 'Items'}
        </TypographyWrapper>
        <TypographyWrapper variant="body2" typographyOverrides={{ style: { textAlign: 'end' } }}>
          {hasShippingLineItems ? (
            <TypographyWrapper
              variant="body2"
              typographyOverrides={{ style: { textAlign: 'end' } }}
              isDynamicValue
            >
              {intl.formatNumber(itemsAndShippingTotal, currencyConfig)}
            </TypographyWrapper>
          ) : (
            'Calculated based on shipping address'
          )}
        </TypographyWrapper>
      </LineItemWrapper>
      <Divider />
    </>
  );
};

interface ItemsLineItemProps {
  listingsTotal: number;
}

const ItemsLineItem: FC<ItemsLineItemProps> = ({ listingsTotal }) => {
  const intl = useIntl();
  const { currencyConfig } = useBrandCountryConfig();

  return (
    <LineItemWrapper>
      <TypographyWrapper variant="body2" weight={TypographyWeight.Bold}>
        Items
      </TypographyWrapper>
      <TypographyWrapper variant="body2" isDynamicValue>
        {intl.formatNumber(listingsTotal, currencyConfig)}
      </TypographyWrapper>
    </LineItemWrapper>
  );
};

interface ShippingLineItemProps {
  lineItems: CheckoutLineItem[];
  showShippingReimbursementTooltip?: boolean;
}

const ShippingLineItem: FC<ShippingLineItemProps> = (props) => {
  const { lineItems, showShippingReimbursementTooltip } = props;

  const intl = useIntl();
  const { currencyConfig } = useBrandCountryConfig();

  const defaultShopStyles = useDefaultShopStyles();

  const { shippingConfig } = useShopConfig();
  const shouldInsureAllPurchases = shippingConfig?.shouldInsureAllPurchases ?? false;

  const shippingTotal = getShippingTotal(lineItems) / 100;
  const shippingLineItems = lineItems.filter((lineItem) => lineItem.code === LineItemCode.Shipping);
  const firstShippingCost = shippingLineItems[0]?.shippingLineItem?.price;

  // Exclude shouldInsureAllPurchases because it dynamically changes with item cost
  const areAllShippingCostsSame =
    shippingLineItems.reduce(
      (acc, lineItem) => acc && firstShippingCost === lineItem.shippingLineItem?.price,
      true
    ) && !shouldInsureAllPurchases;
  const shippingBreakdown =
    !isNil(firstShippingCost) && shippingLineItems.length > 1 && areAllShippingCostsSame
      ? ` (${formatDollarAmount(intl, firstShippingCost, currencyConfig)} × ${
          shippingLineItems.length
        })`
      : '';

  const numListings = lineItems.filter((lineItem) => lineItem.code === LineItemCode.Listing).length;
  const noBundledShippingTotal = firstShippingCost ? (numListings * firstShippingCost) / 100 : 0;
  const savedShippingCost = areAllShippingCostsSame ? noBundledShippingTotal - shippingTotal : 0;
  const isSignedDelivery =
    shippingLineItems[0]?.name === SIGNATURE_CONFIRMATION_SHIPPING_LINE_ITEM_NAME;

  const shippingTooltipMessages = compact([
    isSignedDelivery && 'This shipment requires a signature upon delivery.',
    shouldInsureAllPurchases && 'All shipments are insured.',
    showShippingReimbursementTooltip &&
      'This amount was paid by the buyer to cover shipping costs. The full amount will be reimbursed to you using your payout method of choice.',
  ]);

  return (
    <LineItemWrapper>
      <div>
        <Box display="flex" flexDirection="row" alignItems="center">
          <Typography variant="body2" style={{ fontWeight: 'bold' }} display="inline">
            {shippingLineItems[0]?.name || 'Shipping'}
          </Typography>
          {shippingTooltipMessages.length > 0 && (
            <TypographyWrapper variant="body2" component="span">
              <Box ml={1}>
                <BundlePanelInfoTooltip
                  title={
                    <TypographyWrapper variant="body2">
                      {shippingTooltipMessages.join(' ')}
                    </TypographyWrapper>
                  }
                />
              </Box>
            </TypographyWrapper>
          )}
        </Box>
        <TypographyWrapper
          variant="body2"
          typographyOverrides={{ style: { display: 'inline' } }}
          isDynamicValue
        >
          {shippingBreakdown}
        </TypographyWrapper>
        {savedShippingCost > 0 && (
          <TypographyWrapper
            variant="body2"
            typographyOverrides={{
              style: { color: defaultShopStyles.shopColor60, fontStyle: 'italic' },
            }}
          >
            You saved{' '}
            <TypographyWrapper
              variant="body2"
              isDynamicValue
              typographyOverrides={{ display: 'inline' }}
            >
              {intl.formatNumber(savedShippingCost, currencyConfig)}
            </TypographyWrapper>{' '}
            on bundle shipping
          </TypographyWrapper>
        )}
      </div>
      {/* eslint-disable-next-line no-nested-ternary */}
      {!isEmpty(shippingLineItems) ? (
        shippingTotal > 0 ? (
          <TypographyWrapper
            variant="body2"
            typographyOverrides={{ style: { textAlign: 'end' } }}
            isDynamicValue
          >
            {intl.formatNumber(shippingTotal, currencyConfig)}
          </TypographyWrapper>
        ) : (
          <Typography variant="body2" style={{ textAlign: 'end' }}>
            Free
          </Typography>
        )
      ) : (
        <Typography variant="body2" style={{ textAlign: 'end' }}>
          Calculated based on shipping address
        </Typography>
      )}
    </LineItemWrapper>
  );
};

interface TaxLineItemProps {
  taxTotal: number;
  name?: string;
}

const TaxLineItem: FC<TaxLineItemProps> = (props) => {
  const { taxTotal, name } = props;

  const intl = useIntl();
  const { currencyConfig } = useBrandCountryConfig();

  return (
    <>
      {taxTotal > 0 && (
        <LineItemWrapper>
          <TypographyWrapper
            variant="body2"
            weight={TypographyWeight.Bold}
            typographyOverrides={{ display: 'inline' }}
          >
            {name || 'Tax'}
          </TypographyWrapper>
          <TypographyWrapper
            variant="body2"
            typographyOverrides={{ style: { textAlign: 'end' } }}
            isDynamicValue
          >
            {intl.formatNumber(taxTotal, currencyConfig)}
          </TypographyWrapper>
        </LineItemWrapper>
      )}
    </>
  );
};

interface RefundTotalLineItemProps {
  refundTotal: number;
}

const RefundTotalLineItem: FC<RefundTotalLineItemProps> = ({ refundTotal }) => {
  const intl = useIntl();
  const { currencyConfig } = useBrandCountryConfig();

  return (
    <LineItemWrapper>
      <Typography variant="body2" style={{ fontWeight: 'bold' }} display="inline">
        Refund Total
      </Typography>
      <TypographyWrapper
        variant="body2"
        typographyOverrides={{ style: { textAlign: 'end' } }}
        isDynamicValue
      >
        -{intl.formatNumber(refundTotal, currencyConfig)}
      </TypographyWrapper>
    </LineItemWrapper>
  );
};

interface LineItemBreakdownProps {
  lineItems: CheckoutLineItem[];
  participant: Participant;
  bundleType: BundleType;
  refunds?: CoreRefund[];
  shouldCombineItemsAndShipping?: boolean;
  hasShippingReimbursement?: boolean;
}

const LineItemBreakdown: FC<LineItemBreakdownProps> = (props: LineItemBreakdownProps) => {
  const {
    lineItems: allLineItems,
    participant,
    bundleType,
    refunds,
    shouldCombineItemsAndShipping = false,
    hasShippingReimbursement = false,
  } = props;

  const intl = useIntl();
  const { css: brandCss } = useShopConfig();
  const { currencyConfig } = useBrandCountryConfig();

  const bgcolor = brandCss?.backgroundColor || 'white';

  // Only show the line items that is relevant for the participant
  const lineItems = allLineItems.filter((lineItem) => lineItem.includeFor.includes(participant));
  const refundedLineItemIds = flatten(
    refunds?.map((refund) => refund.refundedLineItems.map((item) => item.id)) || []
  );
  const refundedLineItems = lineItems.filter(
    (lineItem) => lineItem.id && refundedLineItemIds.includes(lineItem.id)
  );
  const totalRefundPrice = calculateLineItemTotal(refundedLineItems);

  const isBuyer = participant === Participant.Buyer;
  const isSeller = participant === Participant.Seller;
  const isShippingVisibleToSeller =
    isSeller && lineItems.some((lineItem) => lineItem.code === LineItemCode.Shipping);
  const listingsTotal = getListingsTotal(lineItems) / 100;
  const shippingTotal = getShippingTotal(lineItems) / 100;
  const discountTotal = getDiscountTotal(lineItems) / 100;
  const returnInsuranceTotal = getReturnInsuranceTotal(lineItems) / 100;
  const taxTotal = getTaxTotal(lineItems) / 100;
  const refundTotal = totalRefundPrice / 100;
  const finalTotal = calculateLineItemTotal(lineItems) / 100 - refundTotal;

  // There should only be one discount applied
  const discountItem = lineItems.find(
    (lineItem) => lineItem.code === LineItemCode.Discount
  )?.discountLineItem;
  const returnInsuranceLineItem = lineItems.find(
    (lineItem) => lineItem.code === LineItemCode.ReturnInsurance
  );

  // Shipping may not be known if a user is on the checkout page and has not entered
  // their shipping address
  const hasShippingLineItems = !isEmpty(
    lineItems.filter((lineItem) => lineItem.code === LineItemCode.Shipping)
  );
  const itemsAndShippingTotal = listingsTotal + shippingTotal;
  const taxLineItem = lineItems.find((lineItem) => lineItem.code === LineItemCode.Tax);

  return (
    <Box display="flex" flexDirection="column" bgcolor={bgcolor}>
      {shouldCombineItemsAndShipping && (
        <ItemsAndShippingLineItem
          hasShippingLineItems={hasShippingLineItems}
          shippingTotal={shippingTotal}
          itemsAndShippingTotal={itemsAndShippingTotal}
        />
      )}
      {!shouldCombineItemsAndShipping && (
        <>
          <ItemsLineItem listingsTotal={listingsTotal} />
          <Divider />
          {(isBuyer || isShippingVisibleToSeller) && bundleType === BundleType.Marketplace && (
            <>
              <ShippingLineItem
                lineItems={lineItems}
                showShippingReimbursementTooltip={hasShippingReimbursement}
              />
              <Divider />
            </>
          )}
        </>
      )}
      {!!discountTotal && (
        <div key={discountItem?.title}>
          <LineItemWrapper>
            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
              {discountItem?.title}
            </Typography>
            <TypographyWrapper
              variant="body2"
              typographyOverrides={{ style: { textAlign: 'end' } }}
              isDynamicValue
            >
              -{intl.formatNumber(discountTotal, currencyConfig)}
            </TypographyWrapper>
          </LineItemWrapper>
          <Divider />
        </div>
      )}
      {!!returnInsuranceTotal && (
        <>
          <LineItemWrapper>
            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
              {returnInsuranceLineItem?.name}
            </Typography>
            <TypographyWrapper
              variant="body2"
              typographyOverrides={{ style: { textAlign: 'end' } }}
              isDynamicValue
            >
              {intl.formatNumber(returnInsuranceTotal, currencyConfig)}
            </TypographyWrapper>
          </LineItemWrapper>
          <Divider />
        </>
      )}
      {!!taxTotal && (
        <>
          <TaxLineItem taxTotal={taxTotal} name={taxLineItem?.name} />
          <Divider />
        </>
      )}
      {!!refundTotal && (
        <>
          <RefundTotalLineItem refundTotal={refundTotal} />
          <Divider />
        </>
      )}
      <LineItemWrapper>
        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
          Total
        </Typography>
        <TypographyWrapper
          variant="body2"
          typographyOverrides={{ style: { color: defaultTreetStyles.red60 } }}
          weight={TypographyWeight.Bold}
          isDynamicValue
        >
          {intl.formatNumber(finalTotal, currencyConfig)}
        </TypographyWrapper>
      </LineItemWrapper>
    </Box>
  );
};

export default LineItemBreakdown;
